import { useEffect, useRef } from "react";
import type { DocumentLink } from "@/models/document-link/DocumentLink";
import { useReferenceSidebarHoverPreviewStore } from "@/stores/reference-sidebar-hover-preview.store";

function extractDocumentLinkIdsFromAnchor(anchor: HTMLElement): string[] | null {
  const combined = anchor.getAttribute("data-document-link-ids");
  if (combined) {
    const ids = combined
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (ids.length) return ids;
  }
  const idAttr = anchor.getAttribute("id");
  if (idAttr) {
    const m = idAttr.match(/^doclink_([a-f0-9-]+)_\d+$/i);
    if (m) return [m[1]];
  }
  return null;
}

interface UseDocumentLinkHandlersProps {
  docRootRef: React.RefObject<HTMLDivElement | null>;
  isContentRendered: boolean;
  documentLinksMap: Map<string, DocumentLink>;
  extractDocumentLinkKeys: (attrs: { id?: string | null }) => string[];
  resolveDocumentLink: (
    keys: string[]
  ) => { linkId: string; link: DocumentLink } | null;
  navigateToDocumentLink: (
    linkId: string,
    event?: React.MouseEvent | MouseEvent
  ) => void;
  isLoadingDocumentLinks: boolean;
}

export function useDocumentLinkHandlers({
  docRootRef,
  isContentRendered,
  documentLinksMap,
  extractDocumentLinkKeys,
  resolveDocumentLink,
  navigateToDocumentLink,
  isLoadingDocumentLinks,
}: UseDocumentLinkHandlersProps) {
  const beginContentHover = useReferenceSidebarHoverPreviewStore(
    (s) => s.beginContentHover
  );
  const endContentHover = useReferenceSidebarHoverPreviewStore(
    (s) => s.endContentHover
  );
  const contentHoverLeaveTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  // Highlight matching reference(s) in the sidebar while hovering a link in the document body
  useEffect(() => {
    if (!docRootRef.current || !isContentRendered) {
      return;
    }

    const docRoot = docRootRef.current;

    const clearContentHoverLeaveTimeout = () => {
      if (contentHoverLeaveTimeoutRef.current) {
        clearTimeout(contentHoverLeaveTimeoutRef.current);
        contentHoverLeaveTimeoutRef.current = null;
      }
    };

    const scheduleEndContentHover = () => {
      clearContentHoverLeaveTimeout();
      contentHoverLeaveTimeoutRef.current = setTimeout(() => {
        endContentHover();
        contentHoverLeaveTimeoutRef.current = null;
      }, 280);
    };

    const attachContentHoverListeners = () => {
      const anchors = docRoot.querySelectorAll<HTMLAnchorElement>(
        'a[id^="doclink_"]'
      );
      anchors.forEach((anchor) => {
        // Multi-link anchors use class doc-link-anchor; hover + portaled menu handled in MultiLinkAnchor
        if (anchor.classList.contains("doc-link-anchor")) {
          return;
        }
        type AnchorWithHover = HTMLAnchorElement & {
          __contentHoverEnter?: () => void;
          __contentHoverLeave?: () => void;
        };
        const a = anchor as AnchorWithHover;
        if (a.__contentHoverEnter) {
          anchor.removeEventListener("pointerenter", a.__contentHoverEnter);
        }
        if (a.__contentHoverLeave) {
          anchor.removeEventListener("pointerleave", a.__contentHoverLeave);
        }

        const handleEnter = () => {
          clearContentHoverLeaveTimeout();
          const ids = extractDocumentLinkIdsFromAnchor(anchor);
          if (ids?.length) {
            beginContentHover(ids);
          }
        };
        const handleLeave = () => {
          scheduleEndContentHover();
        };

        a.__contentHoverEnter = handleEnter;
        a.__contentHoverLeave = handleLeave;
        anchor.addEventListener("pointerenter", handleEnter);
        anchor.addEventListener("pointerleave", handleLeave);
      });
    };

    attachContentHoverListeners();

    const observer = new MutationObserver(() => {
      attachContentHoverListeners();
    });

    observer.observe(docRoot, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearContentHoverLeaveTimeout();
      endContentHover();
      observer.disconnect();
      const anchors = docRoot.querySelectorAll<HTMLAnchorElement>(
        'a[id^="doclink_"]'
      );
      anchors.forEach((anchor) => {
        if (anchor.classList.contains("doc-link-anchor")) {
          return;
        }
        const a = anchor as HTMLAnchorElement & {
          __contentHoverEnter?: () => void;
          __contentHoverLeave?: () => void;
        };
        if (a.__contentHoverEnter) {
          anchor.removeEventListener("pointerenter", a.__contentHoverEnter);
        }
        if (a.__contentHoverLeave) {
          anchor.removeEventListener("pointerleave", a.__contentHoverLeave);
        }
      });
    };
  }, [
    docRootRef,
    isContentRendered,
    beginContentHover,
    endContentHover,
  ]);

  // Add event listeners directly to DOM for document links
  useEffect(() => {
    if (!docRootRef.current || !isContentRendered) {
      return;
    }

    const handleDocumentLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Check if target itself is an anchor tag, or find closest anchor
      let anchor: HTMLAnchorElement | null = null;
      if (target.tagName === "A") {
        anchor = target as HTMLAnchorElement;
      } else {
        anchor = target.closest("a") as HTMLAnchorElement | null;
      }

      if (!anchor) {
        return;
      }

      const linkKeys = extractDocumentLinkKeys({
        id: anchor.getAttribute("id"),
      });

      if (linkKeys.length === 0) {
        return;
      }

      const resolved = resolveDocumentLink(linkKeys);

      if (!resolved) {
        // If still loading, prevent default navigation
        if (isLoadingDocumentLinks) {
          e.preventDefault();
          e.stopPropagation();
        }
        return;
      }

      // Handle document link navigation
      e.preventDefault();
      e.stopPropagation();
      navigateToDocumentLink(resolved.linkId, e);
    };

    const docRoot = docRootRef.current;

    // Also attach listeners directly to existing anchor tags
    const attachListenersToAnchors = () => {
      const anchors =
        docRoot.querySelectorAll<HTMLAnchorElement>('a[id^="doclink_"]');
      anchors.forEach((anchor) => {
        const existingHandler = (
          anchor as HTMLAnchorElement & {
            __documentLinkHandler?: (e: Event) => void;
          }
        ).__documentLinkHandler;
        if (existingHandler) {
          anchor.removeEventListener("click", existingHandler);
        }
        const handler = (e: Event) => {
          const mouseEvent = e as MouseEvent;
          handleDocumentLinkClick(mouseEvent);
        };
        (
          anchor as HTMLAnchorElement & {
            __documentLinkHandler?: (e: Event) => void;
          }
        ).__documentLinkHandler = handler;
        anchor.addEventListener("click", handler);
      });
    };

    // Attach to existing anchors
    attachListenersToAnchors();

    // Also use event delegation on docRoot as backup
    docRoot.addEventListener("click", handleDocumentLinkClick, true); // Use capture phase

    // Watch for new anchor tags being added
    const observer = new MutationObserver(() => {
      attachListenersToAnchors();
    });

    observer.observe(docRoot, {
      childList: true,
      subtree: true,
    });

    return () => {
      docRoot.removeEventListener("click", handleDocumentLinkClick, true);

      // Remove direct listeners from anchors
      const anchors =
        docRoot.querySelectorAll<HTMLAnchorElement>('a[id^="doclink_"]');
      anchors.forEach((anchor) => {
        const anchorWithHandler = anchor as HTMLAnchorElement & {
          __documentLinkHandler?: (e: Event) => void;
        };
        const handler = anchorWithHandler.__documentLinkHandler;
        if (handler) {
          anchor.removeEventListener("click", handler);
          delete anchorWithHandler.__documentLinkHandler;
        }
      });

      observer.disconnect();
    };
  }, [
    docRootRef,
    isContentRendered,
    documentLinksMap,
    extractDocumentLinkKeys,
    resolveDocumentLink,
    navigateToDocumentLink,
    isLoadingDocumentLinks,
  ]);
}
